<template>
  <v-container fluid :fill-height="loading" class="online-test py-0">

    <v-progress-linear style="position: fixed; left: 0; right: 0; margin: 0; z-index: 99;"
                       :style="{top: $vuetify.breakpoint.smAndDown ? '54px' : '62px'}" color="primary lighten-2"
                       height="2" v-show="subtleLoading" :indeterminate="true"></v-progress-linear>

    <hb-loading-indicator v-if="loading" align-middle/>

    <v-fade-transition>
      <v-row v-if="!loading">
        <v-col cols="12" v-if="questions.length > 0"  md="10" offset-md="1" xl="8" offset-xl="2">
          <v-card>
            <v-card-text>
              <v-progress-linear height="2" color="secondary" :value="(currentQuestionIndex / (questions.length - 1)) * 100"></v-progress-linear>
            </v-card-text>
            <v-card-text class="black--text">
              <v-fade-transition>
                <div>
                  <div class="title mb-5">{{ questions[currentQuestionIndex].questionText }}</div>

                  <template v-if="questions[currentQuestionIndex].type === 'SELECT_MANY'">
                    <v-card class="mb-2 option-card" v-for="answer in questions[currentQuestionIndex].answers" :key="answer.id" :class="answer.selected ? 'selected' : ''">
                      <v-checkbox
                        class="pa-2 ma-0"
                        v-model="answer.selected"
                        hide-details
                        :label="answer.answerText"
                      ></v-checkbox>
                    </v-card>
                  </template>

                  <template v-if="questions[currentQuestionIndex].type === 'SELECT_ONE'">
                    <v-radio-group v-model="questions[currentQuestionIndex].selectedAnswer" hide-details>
                      <v-card class="mb-2 option-card" v-for="answer in questions[currentQuestionIndex].answers" :key="answer.id" :class="questions[currentQuestionIndex].selectedAnswer === answer.id ? 'selected' : ''">
                        <v-radio
                          class="pa-2 ma-0"
                          hide-details
                          :label="answer.answerText"
                          :value="answer.id"
                        ></v-radio>
                      </v-card>
                    </v-radio-group>
                  </template>

                  <template v-if="questions[currentQuestionIndex].type === 'SCALE'">
                    <v-row no-gutters class="pt-5">
                      <v-col cols="6" class="pa-0 ma-0 text-left">{{ $t('online_test.not_at_all') }}</v-col>
                      <v-col cols="6" class="pa-0 ma-0 text-right">{{ $t('online_test.completely') }}</v-col>
                    </v-row>
                    <v-slider
                      v-model="questions[currentQuestionIndex].selectedAnswer"
                      max="5"
                      min="1"
                      track-color="#CFD3E6"
                      track-fill-color="#3CDBC0"
                      tick-size="4"
                      ticks="always"
                    ></v-slider>
                  </template>

                  <v-row no-gutters class="mt-7">
                    <v-col cols="6" class="pr-1">
                      <v-btn :disabled="currentQuestionIndex === 0"
                             @click="previousQuestion"
                             block
                             small
                             elevation="0"
                             color="#CFD3E6">
                        <v-icon left>mdi mdi-arrow-left</v-icon>{{ $t('previous') }}
                      </v-btn>
                    </v-col>
                    <v-col cols="6" class="pl-1">
                      <v-btn @click="nextQuestion"
                             :disabled="!currentQuestionAnswered"
                             v-if="currentQuestionIndex < questions.length - 1"
                             block
                             small
                             elevation="0"
                             class="accent-2--text"
                             color="primary">
                        {{ $t('next') }}<v-icon right>mdi mdi-arrow-right</v-icon>
                      </v-btn>
                      <v-btn @click="goToOpenJobs"
                             v-else
                             block
                             small
                             elevation="0"
                             class="accent-2--text"
                             color="primary">
                        {{ $t('done') }}!
                      </v-btn>
                    </v-col>
                  </v-row>

                </div>
              </v-fade-transition>
            </v-card-text>

            <v-card-text class="white--text" style="background-color: #666680;" v-if="jobAds.length > 0">
              <div class="mb-4">{{ $t('online_test.open_jobs_for_you') }}:</div>
              <template v-for="ad in jobAds">
                <v-chip @click="jobLink(ad.id)" :key="ad.id" color="#CFD3E6" small class="mr-1 mb-1">{{ ad.name }}</v-chip>
              </template>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>
  </v-container>
</template>

<script>

import sharedApi from '@/jobs/api/shared';
import OnlineTestService from '@/jobs/service/onlinetest.service';

export default {
  name: 'OnlineTestView',
  data() {
    return {
      loading: true,
      subtleLoading: false,
      currentQuestionIndex: -1,
      previousQuestionIndexes: [],
      questions: [],
      jobAds: [],
    }
  },
  methods: {
    async reloadData() {
      this.loading = true;
      try {
        const questions = await sharedApi.getOnlineTestQuestions();
        const answers = OnlineTestService.getAnswers();
        // set previous answers
        questions.forEach(q => {
          const previousAnswer = answers[q.id];
          if (previousAnswer) {
            if (q.type === 'SCALE') {
              q.selectedAnswer = q.answers.findIndex(a => a.id === previousAnswer[0]) + 1;
            } else if (q.type === 'SELECT_MANY') {
              q.answers.forEach(a => a.selected = previousAnswer.includes(a.id));
            } else if (q.type === 'SELECT_ONE') {
              q.selectedAnswer = previousAnswer[0];
            }
          }
        });
        this.questions = questions;
        await this.nextQuestion();
      } catch (err) {
        this.$handleApiError(err);
      }
      this.loading = false;
    },
    async goToOpenJobs() {
      this.updateAnswers();
      this.$router.push({ name: 'jobs', params: { testView: '1' } });
    },
    jobLink(id) {
      this.$router.push({name: 'apply_job', params: { jobId: id } })
    },
    updateAnswers() {
      const answers = {};
      this.questions.forEach(q => {
        if (q.type === 'SCALE' && q.selectedAnswer) {
          const a = q.answers[q.selectedAnswer - 1];
          if (a) {
            answers[q.id] = [a.id];
          }
        } else if (q.type === 'SELECT_MANY') {
          const qa = q.answers.filter(a => a.selected).map(a => a.id);
          if (qa.length > 0) {
            answers[q.id] = qa;
          }
        } else if (q.type === 'SELECT_ONE' && q.selectedAnswer) {
          answers[q.id] = [q.selectedAnswer];
        }
      });
      return OnlineTestService.addAnswers(answers);
    },
    getAnswerTags() {
      const tags = [];
      this.questions.forEach(q => {
        if (q.type === 'SCALE' && q.selectedAnswer) {
          const a = q.answers[q.selectedAnswer - 1];
          if (a) {
            tags.push.apply(tags, a.tags);
          }
        } else if (q.type === 'SELECT_MANY') {
          q.answers.forEach(a => {
            if (a.selected) {
              tags.push.apply(tags, a.tags);
            }
          });
        } else if (q.type === 'SELECT_ONE' && q.selectedAnswer) {
          const a = q.answers.find(a => a.id === q.selectedAnswer);
          tags.push.apply(tags, a.tags);
        }
      });
      return tags;
    },
    async nextQuestion() {
      if (this.currentQuestionIndex >= 0) {
        this.previousQuestionIndexes.push(this.currentQuestionIndex);
      }
      try {
        const answers = this.updateAnswers();
        const answerTags = this.getAnswerTags();
        let skip = false;
        do {
          this.currentQuestionIndex++;
          if (this.currentQuestionIndex >= 0 && this.currentQuestionIndex < this.questions.length) {
            const next = this.questions[this.currentQuestionIndex];
            // skip if not all required tags are found, or some exclusive tags found
            skip = !next.requiredTags.map(t => t.description).every(t => answerTags.includes(t)) ||
                   next.exclusiveTags.map(t => t.description).some(t => answerTags.includes(t));
          } else if (this.currentQuestionIndex === this.questions.length) {
            this.$router.push({ name: 'jobs', params: { testView: '1' } });
            return;
          }
        } while (skip);
        this.jobAds = await sharedApi.getJobAds(answers);
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    previousQuestion() {
      this.currentQuestionIndex = this.previousQuestionIndexes.pop();
    },
  },
  computed: {
    currentQuestionAnswered() {
      const q = this.questions[this.currentQuestionIndex];
      if (q.type === 'FREE_TEXT') {
        return true;
      } else if (q.type === 'SELECT_MANY') {
        return q.answers.some(a => a.selected);
      } else {
        return !!q.selectedAnswer;
      }
    },
  },
  mounted() {
    this.reloadData();
  }
}
</script>

<style lang="scss" scoped>
  .option-card {
    border: 2px solid #CFD3E6;

    &.selected {
      border-color: #3CDBC0;
    }
  }
</style>
